/**
 * useLrc — fetch and parse LRC synchronized lyrics from CDN.
 * LRC file lives at the same URL as the track's audio, with .lrc extension.
 */
import { useState, useEffect, useRef, useCallback } from "react";

function parseLrc(text) {
  const items = [];
  const lineRegex = /\[(\d{2}):(\d{2})[.:](\d{2,3})\]/;
  for (const line of text.split("\n")) {
    const m = line.match(lineRegex);
    if (!m) continue;
    const mins = parseInt(m[1], 10);
    const secs = parseInt(m[2], 10);
    const ms = parseInt(m[3].padEnd(3, "0"), 10);
    const time = mins * 60 + secs + ms / 1000;
    const textContent = line.replace(lineRegex, "").trim();
    if (textContent) {
      items.push({ time, text: textContent });
    }
  }
  items.sort((a, b) => a.time - b.time);
  return items;
}

/**
 * @param {object|null} track — currentTrack from PlayerContext
 * @param {number} currentTime — audio.currentTime
 * @returns {{ lyrics: Array<{time,text}>, currentLine: {time,text}|null, loading: boolean }}
 */
export default function useLrc(track, currentTime) {
  const [lyrics, setLyrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const trackIdRef = useRef(null);

  // Fetch LRC when track changes
  useEffect(() => {
    if (!track || !track.url) {
      setLyrics([]);
      return;
    }
    if (track.id === trackIdRef.current) return;
    trackIdRef.current = track.id;

    const lrcUrl = track.url.replace(/\.mp3$/i, ".lrc");
    setLoading(true);
    fetch(lrcUrl)
      .then((r) => {
        if (!r.ok) throw new Error("No LRC");
        return r.text();
      })
      .then((text) => {
        const parsed = parseLrc(text);
        setLyrics(parsed);
      })
      .catch(() => {
        setLyrics([]);
      })
      .finally(() => setLoading(false));
  }, [track]);

  // Find current line
  const currentLine = useCallback(() => {
    if (!lyrics.length || currentTime == null) return null;
    let current = null;
    for (const item of lyrics) {
      if (item.time <= currentTime) {
        current = item;
      } else {
        break;
      }
    }
    return current;
  }, [lyrics, currentTime]);

  return { lyrics, currentLine: currentLine(), loading };
}
